import { useState } from 'react'
import type { PipelineOutcome, PipelineStage } from '@/types/crm'
import { COLOR_PRESETS } from '../defaults'

interface Props {
  stages: PipelineStage[]
  outcomes: PipelineOutcome[]
  /** Stage keys that have at least one lead assigned — these cannot be deleted. */
  usedKeys: Set<string>
  onSave: (stages: PipelineStage[], outcomes: PipelineOutcome[]) => Promise<void>
  readOnly: boolean
}

function nextColor(current: string) {
  const idx = COLOR_PRESETS.findIndex((p) => p.color.toLowerCase() === current.toLowerCase())
  return COLOR_PRESETS[(idx + 1) % COLOR_PRESETS.length]
}

/**
 * Pipeline stage editor.
 *
 * Edits are staged locally and committed with Save, so a half-typed label never
 * lands in Firestore (every lead's badge and stepper reads this document live).
 * Delete is blocked for stages that still have leads — a guard rail against
 * orphaning data; the same check is enforced in the Firestore rules.
 */
export function StageEditor({ stages, outcomes, usedKeys, onSave, readOnly }: Props) {
  const [draftStages, setDraftStages] = useState<PipelineStage[]>(stages)
  const [draftOutcomes, setDraftOutcomes] = useState<PipelineOutcome[]>(outcomes)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // Adopt upstream changes only while the editor is clean, so a live update
  // from another admin does not wipe an in-progress edit.
  if (!dirty && (draftStages !== stages || draftOutcomes !== outcomes)) {
    setDraftStages(stages)
    setDraftOutcomes(outcomes)
  }

  const mutate = (fn: () => void) => {
    fn()
    setDirty(true)
  }

  const renameStage = (key: string, label: string) =>
    mutate(() => setDraftStages((prev) => prev.map((s) => (s.key === key ? { ...s, label } : s))))

  const cycleStageColor = (key: string) =>
    mutate(() =>
      setDraftStages((prev) =>
        prev.map((s) => (s.key === key ? { ...s, ...nextColor(s.color) } : s)),
      ),
    )

  const move = (index: number, delta: number) =>
    mutate(() =>
      setDraftStages((prev) => {
        const target = index + delta
        if (target < 0 || target >= prev.length) return prev
        const arr = [...prev]
        ;[arr[index], arr[target]] = [arr[target], arr[index]]
        return arr
      }),
    )

  const remove = (key: string) =>
    mutate(() => setDraftStages((prev) => prev.filter((s) => s.key !== key)))

  const toggleHidden = (key: string) =>
    mutate(() =>
      setDraftStages((prev) =>
        prev.map((s) => (s.key === key ? { ...s, hidden: !s.hidden } : s)),
      ),
    )

  const add = () =>
    mutate(() =>
      setDraftStages((prev) => {
        const preset = COLOR_PRESETS[(prev.length + draftOutcomes.length) % COLOR_PRESETS.length]
        return [
          ...prev,
          {
            key: `stage_${Math.random().toString(36).slice(2, 9)}`,
            label: 'New stage',
            color: preset.color,
            bg: preset.bg,
            builtin: false,
          },
        ]
      }),
    )

  const renameOutcome = (key: string, label: string) =>
    mutate(() =>
      setDraftOutcomes((prev) => prev.map((o) => (o.key === key ? { ...o, label } : o))),
    )

  const cycleOutcomeColor = (key: string) =>
    mutate(() =>
      setDraftOutcomes((prev) =>
        prev.map((o) => (o.key === key ? { ...o, ...nextColor(o.color) } : o)),
      ),
    )

  const save = async () => {
    setSaving(true)
    try {
      await onSave(draftStages, draftOutcomes)
      setDirty(false)
    } finally {
      setSaving(false)
    }
  }

  const canDelete = (s: PipelineStage) =>
    !readOnly && !usedKeys.has(s.key) && !s.builtin && draftStages.length > 1

  return (
    <div className="admincard">
      <div className="card-label">Pipeline stages</div>

      <div className="stagelist">
        {draftStages.map((s, i) => (
          <div className="stagerow" key={s.key}>
            <button
              className="stagerow__swatch"
              style={{ background: s.color }}
              title="Click to change colour"
              onClick={() => cycleStageColor(s.key)}
              disabled={readOnly}
              aria-label={`Change colour for ${s.label}`}
            />
            <input
              className="stagerow__input"
              value={s.label}
              onChange={(e) => renameStage(s.key, e.target.value)}
              disabled={readOnly}
              aria-label="Stage name"
            />
            <button
              className={`stagerow__btn${s.hidden ? ' stagerow__btn--hidden' : ''}`}
              onClick={() => toggleHidden(s.key)}
              disabled={readOnly}
              title={
                s.hidden
                  ? 'Hidden from the lead list — click to show it there by default'
                  : 'Shown in the lead list — click to hold it back'
              }
              aria-pressed={Boolean(s.hidden)}
              aria-label={`Toggle whether ${s.label} appears in the lead list`}
            >
              {s.hidden ? '◌' : '●'}
            </button>
            <button
              className="stagerow__btn"
              onClick={() => move(i, -1)}
              disabled={readOnly || i === 0}
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              className="stagerow__btn"
              onClick={() => move(i, 1)}
              disabled={readOnly || i === draftStages.length - 1}
              aria-label="Move down"
            >
              ↓
            </button>
            <button
              className="stagerow__btn stagerow__btn--danger"
              onClick={() => remove(s.key)}
              disabled={!canDelete(s)}
              title={
                usedKeys.has(s.key)
                  ? 'Stages with leads assigned cannot be deleted'
                  : s.builtin
                    ? 'Built-in stages are referenced by automation and cannot be deleted'
                    : 'Delete stage'
              }
              aria-label={`Delete ${s.label}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <button className="addstage" onClick={add} disabled={readOnly}>
        + Add stage
      </button>

      <div className="card-label">Final outcomes</div>
      <div className="stagelist">
        {draftOutcomes.map((o) => (
          <div className="stagerow" key={o.key}>
            <button
              className="stagerow__swatch"
              style={{ background: o.color }}
              title="Click to change colour"
              onClick={() => cycleOutcomeColor(o.key)}
              disabled={readOnly}
              aria-label={`Change colour for ${o.label}`}
            />
            <input
              className="stagerow__input"
              value={o.label}
              onChange={(e) => renameOutcome(o.key, e.target.value)}
              disabled={readOnly}
              aria-label="Outcome name"
            />
          </div>
        ))}
      </div>

      <div className="micro admincard__note">
        Built-in stages and stages already in use can’t be deleted. Outcomes are fixed as a pair.
        <br />
        The ● / ◌ toggle controls whether a stage shows up in the lead list. A hollow circle means
        it is held back — <strong>Unverified</strong> ships that way, so leads that arrived without
        enough information to work don’t pad out everyone’s list. They are still one click away:
        the lead list says how many are hidden and offers to show them.
      </div>

      {!readOnly && (
        <div className="admincard__actions">
          <button className="btn btn--primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save stages' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  )
}
